import * as R from 'ramda';
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

export type MediaSizes = 'xs' | 'sm' | 'md' | 'lg';
type MediaFormats = 'webp' | 'png';
export const EMPTY_IMAGE_PATH = '/images/empty-image.png';

export const getMediaToSize = (
  url?: string | null,
  size?: MediaSizes,
  format?: MediaFormats,
): string => {
  if (R.isNil(url) || url?.length === 0) {
    return EMPTY_IMAGE_PATH;
  }

  if (!size) {
    return url;
  }

  const extension = url.substr(url.lastIndexOf('.') + 1);
  if (['webp', 'png', 'jpeg', 'jpg'].includes(extension.toLocaleLowerCase())) {
    return url;
  }

  return `${url}-${size}.${format || 'png'}`;
};

export const checkWebpSupport = async () => {
  if (!window.createImageBitmap) {
    return false;
  }

  const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
  const blob = await fetch(webpData).then((response) => response.blob());
  return createImageBitmap(blob).then(
    () => true,
    () => false,
  );
};

type MediaApplicationContextProps = {
  webpEnabled: boolean;
  getMediaFromSize: (url?: string | null, size?: MediaSizes, format?: MediaFormats) => string;
};

type MediaApplicationContextProviderProps = {
  children: React.ReactNode;
};

const MediaApplicationContext = createContext<MediaApplicationContextProps>({
  webpEnabled: false,
  getMediaFromSize: () => EMPTY_IMAGE_PATH,
});

//TODO: ISSO NÃO PRECISA SER UM CONTEXTO, PELO MENOS NÃO APLICADO NO NÍVEL DO App.tsx
export const MediaApplicationContextProvider = ({
  children,
}: MediaApplicationContextProviderProps) => {
  const [webpEnabled, setWebpEnabled] = useState<boolean>(false);

  const validateWebpSupport = async () => {
    const supports = await checkWebpSupport();
    setWebpEnabled(supports);
  };

  useEffect(() => {
    validateWebpSupport();
  }, []);

  const getMediaFromSize = (url?: string | null, size?: MediaSizes, format?: MediaFormats) => {
    let preferredFormat = format;
    if (!preferredFormat) {
      preferredFormat = webpEnabled ? 'webp' : 'png';
    }

    return getMediaToSize(url, size, preferredFormat);
  };

  const values: MediaApplicationContextProps = {
    webpEnabled,
    getMediaFromSize,
  };

  return (
    <MediaApplicationContext.Provider value={values}>{children}</MediaApplicationContext.Provider>
  );
};

export const useMediaApplicationContext = () =>
  useContext<MediaApplicationContextProps>(MediaApplicationContext);
